import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskLineInView } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Your name", type: "text", required: true, span: 1 },
  { name: "company", label: "Company", type: "text", required: false, span: 1 },
  { name: "email", label: "Email", type: "email", required: true, span: 1 },
  { name: "phone", label: "Phone", type: "tel", required: false, span: 1 },
];

const EMPTY = { name: "", company: "", email: "", phone: "", message: "" };

export const Enquiry = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setDone(true);
      setForm(EMPTY);
      toast.success("Enquiry sent! We'll get back to you shortly.");
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Failed to send enquiry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="bg-[var(--surface)] py-16 md:py-36">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--brand)]">
                Request a quote
              </span>
            </Reveal>
            <h2 className="font-display font-semibold tracking-tighter leading-[0.98] mt-5 text-4xl md:text-5xl">
              <MaskLineInView>Tell us what</MaskLineInView>
              <MaskLineInView delay={0.08}>you need.</MaskLineInView>
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-6 text-lg leading-relaxed text-[var(--muted)]">
                Share your volume, cap type and timeline. Our team replies within one business day
                with pricing and lead time.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                data-testid="enquiry-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-3xl border border-[var(--brand)]/30 bg-white p-12 text-center"
              >
                <CheckCircle2 className="mx-auto text-[var(--brand)]" size={44} />
                <h3 className="font-display text-2xl font-medium mt-5 tracking-tight">Enquiry received</h3>
                <p className="mt-3 text-[var(--muted)]">
                  Thanks for reaching out — we'll be in touch shortly.
                </p>
                <button
                  data-testid="enquiry-reset"
                  onClick={() => setDone(false)}
                  className="mt-6 text-sm font-semibold text-[var(--brand)] hover:text-[var(--brand-hover)] transition-colors"
                >
                  Send another enquiry
                </button>
              </motion.div>
            ) : (
              <form
                data-testid="enquiry-form"
                onSubmit={submit}
                className="rounded-3xl border border-slate-200 bg-white p-7 md:p-9 grid grid-cols-1 sm:grid-cols-2 gap-5"
              >
                {FIELDS.map((f) => (
                  <label key={f.name} className="flex flex-col gap-2">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
                      {f.label} {f.required && <span className="text-[var(--brand)]">*</span>}
                    </span>
                    <input
                      data-testid={`enquiry-${f.name}`}
                      type={f.type}
                      required={f.required}
                      value={form[f.name]}
                      onChange={update(f.name)}
                      className="rounded-xl border border-slate-200 bg-[var(--surface)] px-4 py-3 text-[var(--ink)] outline-none focus:border-[var(--brand)] focus:bg-white transition-[border-color,background-color]"
                    />
                  </label>
                ))}
                <label className="flex flex-col gap-2 sm:col-span-2">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
                    Message <span className="text-[var(--brand)]">*</span>
                  </span>
                  <textarea
                    data-testid="enquiry-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="e.g. 500,000 ALASKA 27mm caps / month — need pricing & lead time."
                    className="rounded-xl border border-slate-200 bg-[var(--surface)] px-4 py-3 text-[var(--ink)] outline-none resize-none placeholder:text-slate-400 focus:border-[var(--brand)] focus:bg-white transition-[border-color,background-color]"
                  />
                </label>
                <button
                  data-testid="enquiry-submit"
                  type="submit"
                  disabled={loading}
                  className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] px-7 py-4 text-sm font-semibold text-white hover:bg-[var(--brand-hover)] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 size={17} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Send enquiry <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
